import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { EmbedPdfViewer } from '@/components/PdfPreview/EmbedPdfViewer';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/contexts/LanguageContext';
import { InvoiceService } from '@/lib/services/invoiceService';

interface PdfPreviewProps {
  pdfBytes: Uint8Array | null;
  onClose: () => void;
  formData?: any; // Form data for invoice generation
}

const BREVO_API_URL = import.meta.env.VITE_BREVO_API_URL;
const DEFAULT_BREVO_FROM_EMAIL = import.meta.env.VITE_MAIL_RECIPIENT;
const DEFAULT_BREVO_FROM_NAME = import.meta.env.VITE_FROM;

function isValidEmail(value: unknown): value is string {
  return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const element of bytes) {
    binary += String.fromCodePoint(element ?? 0);
  }

  return btoa(binary);
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim().length > 0) {
    return error;
  }

  return 'Unknown error';
}

function getApiErrorDetails(payload: unknown): string {
  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const maybeError = payload as Record<string, unknown>;
  return [maybeError.message, maybeError.error, maybeError.code]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' - ');
}

async function generateInvoicePdfBytes(formData: unknown): Promise<Uint8Array> {
  return InvoiceService.generateInvoicePdf(
    formData as Parameters<typeof InvoiceService.generateInvoicePdf>[0],
  );
}

export function PdfPreview({ pdfBytes, onClose, formData }: Readonly<PdfPreviewProps>): ReactNode {
  const { t } = useLanguage();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState<boolean>(false);

  useEffect(() => {
    if (pdfBytes) {
      // Create a blob URL from the PDF bytes
      const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

      // Clean up the URL when component unmounts
      return (): void => {
        URL.revokeObjectURL(url);
      };
    }
  }, [pdfBytes]);

  const handleDownloadInvoice = async (): Promise<void> => {
    if (!formData) return;

    setIsDownloadingInvoice(true);

    try {
      const invoiceBytes = await generateInvoicePdfBytes(formData);
      const blob = new Blob([invoiceBytes as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'invoice.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error(`${t.downloadInvoiceError}: ${extractErrorMessage(error)}`);
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  const handleSendPdf = async (): Promise<void> => {
    if (!pdfBytes) return;

    setIsSending(true);
    toast.loading(t.sendingEmail);

    try {
      if (!formData) {
        toast.dismiss();
        toast.error(`${t.emailError}: missing form data for invoice generation.`);
        return;
      }

      const apiKey = import.meta.env.VITE_BREVO_API_KEY;
      const toEmail = import.meta.env.VITE_MAIL_RECIPIENT;
      const fromName = import.meta.env.VITE_FROM || DEFAULT_BREVO_FROM_NAME;

      if (!BREVO_API_URL) {
        toast.dismiss();
        toast.error(`${t.emailError}: missing VITE_BREVO_API_URL in environment.`);
        return;
      }

      if (!apiKey) {
        toast.dismiss();
        toast.error(`${t.emailError}: missing VITE_BREVO_API_KEY in environment.`);
        return;
      }

      if (!isValidEmail(toEmail)) {
        toast.dismiss();
        toast.error(`${t.emailError}: VITE_MAIL_RECIPIENT is not a valid email.`);
        return;
      }

      if (!isValidEmail(DEFAULT_BREVO_FROM_EMAIL)) {
        toast.dismiss();
        toast.error(`${t.emailError}: VITE_MAIL_RECIPIENT is not a valid sender email.`);
        return;
      }

      const invoiceBytes = await generateInvoicePdfBytes(formData);

      const payload = {
        sender: {
          email: DEFAULT_BREVO_FROM_EMAIL,
          name: fromName,
        },
        to: [{ email: toEmail }],
        subject: 'Company Registration PDF',
        textContent: `${isValidEmail(formData?.email) ? formData.email : 'A user'} registered a company.`,
        attachment: [
          {
            name: 'company-registration.pdf',
            content: toBase64(pdfBytes),
          },
          {
            name: 'invoice.pdf',
            content: toBase64(invoiceBytes),
          },
        ],
        ...(isValidEmail(formData?.email) ? { replyTo: { email: formData.email } } : {}),
      };

      const response = await fetch(BREVO_API_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'api-key': apiKey,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const details = getApiErrorDetails(result);
        const status = `${response.status} ${response.statusText}`.trim();
        toast.error(
          details
            ? `${t.emailError}: ${details} (${status})`
            : `${t.emailError}: request failed (${status})`,
        );
        return;
      }

      toast.dismiss();
      toast.success(`${t.emailSent}`);
    } catch (error) {
      console.error('Error sending PDF via email:', error);
      toast.dismiss();
      toast.error(`${t.emailError}: ${extractErrorMessage(error)}`);
    } finally {
      setIsSending(false);
    }
  };

  if (!pdfUrl) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex h-[80vh] w-full max-w-4xl flex-col rounded-lg bg-white p-4 shadow-xl dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold dark:text-white">{t.officialForm}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t.pdfPreview}</p>
          </div>
          <Button variant="outline" onClick={onClose}>
            {t.close}
          </Button>
        </div>
        <div className="flex-1 overflow-hidden rounded border border-gray-200 bg-white">
          <EmbedPdfViewer src={pdfUrl} />
        </div>
        <div className="mt-4 flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose}>
            {t.close}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              // Create a link and click it to download
              const link = document.createElement('a');
              link.href = pdfUrl;
              link.download = 'registration.pdf';
              link.click();
            }}
          >
            {t.download}
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadInvoice}
            disabled={isDownloadingInvoice || !formData}
          >
            {isDownloadingInvoice ? t.loading : t.downloadInvoice}
          </Button>
          <Button
            variant="default"
            onClick={handleSendPdf}
            disabled={isSending}
            className="bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
          >
            {t.send}
          </Button>
        </div>
      </div>
    </div>
  );
}
